import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Bell, CalendarDays, FileDown } from "lucide-react";
import { Color } from "../../../constants/color";
import { DocumentData, StatoAttestato } from "../../../types";
import { dateOptionsNumeric, getLocale } from "../../../utils/utils";
import { downloadCertificate } from "../../../features/certificate/utils";
import Suggerimento from "../../tooltip/suggerimento";

// Modulo di rendering condiviso della riga attestato: badge di stato con le
// date, info sulla prenotazione e azione di download. Lo stato è quello del
// backend: qui si sceglie solo colore ed etichetta.

const Badge = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: ${(props) => props.$color};
`;

/*
 Etichetta e data dello stato. Incolonnate di default, com'e' nelle tabelle attestati, dove la
 colonna e' stretta; su una riga sola dove c'e' larghezza e l'incolonnamento sparpaglierebbe due
 pezzi della stessa frase (`inline`).
*/
const BadgeTesto = styled.div<{ $inline: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.$inline ? "row" : "column")};
  flex-wrap: wrap;
  align-items: ${(props) => (props.$inline ? "baseline" : "stretch")};
  gap: ${(props) => (props.$inline ? "0.3rem" : "0.2rem")};
`;

const Info = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: ${Color.BLUE};
`;

const IconaDownload = styled(FileDown)`
  justify-self: center;
  cursor: pointer;
`;

const COLORE_STATO: Record<StatoAttestato, string> = {
  valido: Color.GREEN,
  in_scadenza: Color.ORANGE,
  scaduto: Color.RED,
  mancante: Color.RED,
  superato: Color.DARK_GREY,
};

function formattaData(data: Date | undefined): string | undefined {
  if (!data) return undefined;
  const formattata = new Date(data).toLocaleDateString(
    getLocale(),
    dateOptionsNumeric
  );
  return formattata === "Invalid Date" ? undefined : formattata;
}

function chiaveEtichetta(stato: StatoAttestato, conData: boolean): string {
  switch (stato) {
    case "valido":
      return conData
        ? "attestato-stato-valido-fino-al"
        : "attestato-stato-valido";
    case "in_scadenza":
      return "attestato-stato-scade-il";
    case "scaduto":
      return "attestato-stato-scaduto-il";
    case "superato":
      return conData
        ? "attestato-stato-superato-il"
        : "attestato-stato-superato";
    case "mancante":
    default:
      return "attestato-stato-mancante";
  }
}

export function BadgeStatoAttestato({
  attestato,
  inline = false,
}: {
  attestato: DocumentData;
  // Etichetta e data sulla stessa riga invece che incolonnate.
  inline?: boolean;
}) {
  const { t } = useTranslation();
  const data =
    attestato.stato === "mancante"
      ? undefined
      : formattaData(attestato.metadata.data_scadenza);
  const colore = COLORE_STATO[attestato.stato] ?? Color.NONE;
  return (
    <Badge $color={colore}>
      <Bell color={colore} fill={colore} width={24} height={24} />
      <BadgeTesto $inline={inline}>
        <p>{t(chiaveEtichetta(attestato.stato, data !== undefined))}</p>
        {data !== undefined && <p>{data}</p>}
      </BadgeTesto>
    </Badge>
  );
}

export function InfoPrenotazione({ attestato }: { attestato: DocumentData }) {
  const { t } = useTranslation();
  const data = formattaData(attestato.metadata.data_prenotazione);
  if (attestato.metadata.data_prenotazione === undefined) {
    return <Info />;
  }
  return (
    <Info>
      <CalendarDays color={Color.BLUE} width={24} height={24} />
      <p>{`${t("attestato-prenotato-il")} ${data ?? ""}`}</p>
    </Info>
  );
}

export function ScaricaAttestato({ attestato }: { attestato: DocumentData }) {
  const { t } = useTranslation();
  if (!attestato.fileName) {
    return <p></p>;
  }
  return (
    <Suggerimento testo={t("attestati-aiuto-scarica-pdf")}>
      <IconaDownload
        color={Color.LIGHT_BLUE}
        width={24}
        height={24}
        onClick={() => downloadCertificate(attestato)}
      />
    </Suggerimento>
  );
}
